export type AudioSettings = {
  sfxVolume: number;
  musicVolume: number;
};

const AUDIO_SETTINGS_KEY = 'poker_audio_settings_v1';
const AUDIO_SETTINGS_EVENT = 'clair-holdem:audio-settings-changed';
const DEFAULT_SETTINGS: AudioSettings = {
  sfxVolume: 1,
  musicVolume: 1,
};

const clampVolume = (value: number): number => {
  if (!Number.isFinite(value)) return 1;
  return Math.min(Math.max(value, 0), 1);
};

export const loadAudioSettings = (): AudioSettings => {
  try {
    const stored = localStorage.getItem(AUDIO_SETTINGS_KEY);
    if (!stored) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(stored) as Partial<AudioSettings>;
    return {
      sfxVolume: clampVolume(parsed.sfxVolume ?? DEFAULT_SETTINGS.sfxVolume),
      musicVolume: clampVolume(parsed.musicVolume ?? DEFAULT_SETTINGS.musicVolume),
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
};

export const saveAudioSettings = (settings: AudioSettings): void => {
  const normalized: AudioSettings = {
    sfxVolume: clampVolume(settings.sfxVolume),
    musicVolume: clampVolume(settings.musicVolume),
  };
  try {
    localStorage.setItem(AUDIO_SETTINGS_KEY, JSON.stringify(normalized));
  } catch {
    // Persisting settings is best-effort; ignore storage errors
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<AudioSettings>(AUDIO_SETTINGS_EVENT, { detail: normalized }));
  }
};

export const onAudioSettingsChanged = (listener: (settings: AudioSettings) => void): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const customEventListener = (event: Event) => {
    const detail = (event as CustomEvent<AudioSettings>).detail;
    if (detail) {
      listener({
        sfxVolume: clampVolume(detail.sfxVolume),
        musicVolume: clampVolume(detail.musicVolume),
      });
      return;
    }

    listener(loadAudioSettings());
  };

  const storageListener = (event: StorageEvent) => {
    if (event.key && event.key !== AUDIO_SETTINGS_KEY) return;
    listener(loadAudioSettings());
  };

  window.addEventListener(AUDIO_SETTINGS_EVENT, customEventListener);
  window.addEventListener('storage', storageListener);

  return () => {
    window.removeEventListener(AUDIO_SETTINGS_EVENT, customEventListener);
    window.removeEventListener('storage', storageListener);
  };
};
